
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

type StreakData = {
  postStreak: number;
  likeStreak: number;
  bestPostStreak: number;
  bestLikeStreak: number;
  lastPosted: string | null;
  lastLiked: string | null;
  streakUpdated: string | null;
};

const INITIAL_STREAK_DATA: StreakData = {
  postStreak: 0,
  likeStreak: 0,
  bestPostStreak: 0,
  bestLikeStreak: 0,
  lastPosted: null,
  lastLiked: null,
  streakUpdated: null
};

export const useStreakTracking = () => {
  const { user } = useAuth();
  const [streakData, setStreakData] = useState<StreakData>(INITIAL_STREAK_DATA);
  
  // Load streak data from localStorage on component mount
  useEffect(() => {
    if (!user?.id) return;
    
    const storedData = localStorage.getItem(`streak-data-${user.id}`);
    if (storedData) {
      try {
        setStreakData(JSON.parse(storedData));
      } catch (error) {
        console.error('Failed to parse streak data:', error);
      }
    }
  }, [user?.id]);
  
  // Save streak data to localStorage whenever it changes
  useEffect(() => {
    if (!user?.id) return;
    
    localStorage.setItem(`streak-data-${user.id}`, JSON.stringify(streakData));
  }, [streakData, user?.id]);
  
  // Check if the streak should be reset (if more than 24 hours have passed since last activity)
  const checkStreakStatus = () => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const shouldResetPostStreak = streakData.lastPosted && 
      new Date(streakData.lastPosted) < yesterday;
    
    const shouldResetLikeStreak = streakData.lastLiked && 
      new Date(streakData.lastLiked) < yesterday;
    
    if (shouldResetPostStreak || shouldResetLikeStreak) {
      setStreakData(prev => ({
        ...prev,
        postStreak: shouldResetPostStreak ? 0 : prev.postStreak,
        likeStreak: shouldResetLikeStreak ? 0 : prev.likeStreak,
        streakUpdated: now.toISOString()
      }));
    }
  };
  
  // Check streak status on component mount and whenever streakData changes
  useEffect(() => {
    checkStreakStatus();
  }, [streakData.lastPosted, streakData.lastLiked]);
  
  // Record a post to increment the post streak
  const recordPost = () => {
    const now = new Date().toISOString();
    
    setStreakData(prev => {
      const newPostStreak = prev.lastPosted ? prev.postStreak + 1 : 1;
      return {
        ...prev,
        postStreak: newPostStreak,
        bestPostStreak: Math.max(newPostStreak, prev.bestPostStreak),
        lastPosted: now,
        streakUpdated: now
      };
    });
  };
  
  // Record a like to increment the like streak
  const recordLike = () => {
    const now = new Date().toISOString();
    
    setStreakData(prev => {
      const newLikeStreak = prev.lastLiked ? prev.likeStreak + 1 : 1;
      return {
        ...prev,
        likeStreak: newLikeStreak,
        bestLikeStreak: Math.max(newLikeStreak, prev.bestLikeStreak),
        lastLiked: now,
        streakUpdated: now
      };
    });
  };
  
  // Add to batch (used by the BulletinForm component)
  const addToBatch = async (bulletin: any) => {
    console.log('Posted bulletin:', bulletin);
    recordPost();
    return bulletin;
  };
  
  return {
    streakData,
    recordPost,
    recordLike,
    addToBatch
  };
};
